export interface CommunityStats {
  submissions: number
  avg_score: number
  median_score: number
  high_score: number
  scores: { score: number; count: number }[]
}

/** Below this many players a percentile says little, so none is shown. */
export const MIN_PLAYERS = 20

/**
 * The share of players who scored at least `mine`, as "top x%", counting
 * `mine` as already on the scoreboard and ties in the player's favour.
 * Null until the day has MIN_PLAYERS.
 */
export function topPercent(stats: CommunityStats, mine: number): number | null {
  const players = stats.submissions
  if (players < MIN_PLAYERS) return null
  const above = stats.scores.filter((s) => s.score > mine).reduce((n, s) => n + s.count, 0)
  return Math.min(100, Math.max(1, Math.ceil(((above + 1) * 100) / players)))
}

/** Where `mine` ranks on a day's scoreboard, assuming it is already counted. */
export function standing(stats: CommunityStats, mine: number): string {
  const players = stats.submissions
  if (players <= 1) return 'First on the scoreboard for this day!'
  if (mine >= stats.high_score) return 'Top score on the scoreboard!'
  const top = topPercent(stats, mine)
  if (top === null) return `Early days: ${players} players so far`
  return `Top ${top}% of players`
}

export interface Cutoff {
  /** e.g. 10 for "top 10%". */
  percent: number
  /** The lowest score that still makes it. */
  score: number
}

/** The score needed for the top 1%, 10%, 25% and 50%, leaving out bands smaller than one player. */
export function cutoffs(stats: CommunityStats): Cutoff[] {
  const players = stats.submissions
  if (players < MIN_PLAYERS) return []
  const best = [...stats.scores].sort((a, b) => b.score - a.score)
  return [1, 10, 25, 50]
    .filter((percent) => players * percent >= 100)
    .map((percent) => {
      // The last place inside the band: a player there is still in the top `percent`.
      const place = Math.floor((players * percent) / 100)
      let seen = 0
      const at = best.find((s) => (seen += s.count) >= place)!
      return { percent, score: at.score }
    })
}

export interface Band {
  from: number
  to: number
  count: number
  mine: boolean
}

/** The day's scores grouped into at most `bands` equal ranges, lowest first. */
export function distribution(stats: CommunityStats, mine: number, bands = 8): Band[] {
  if (!stats.scores.length) return []
  const scores = stats.scores.map((s) => s.score)
  const lo = Math.min(...scores, mine)
  const hi = Math.max(...scores, mine)
  const width = Math.max(1, Math.ceil((hi - lo + 1) / bands))
  const out: Band[] = []
  for (let from = lo; from <= hi; from += width) {
    const to = Math.min(hi, from + width - 1)
    const count = stats.scores.filter((s) => s.score >= from && s.score <= to).reduce((n, s) => n + s.count, 0)
    out.push({ from, to, count, mine: mine >= from && mine <= to })
  }
  return out
}
